import React, { useMemo, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

const numberFormat = { type: "fixedPoint", precision: 2 };

/**
 * Reusable WM serial-number grid backed by wm/Serialnumber/GetSerialNumberCodes
 * (server-side DataSourceLoader), filtered by the selected item (`itemRowId`
 * → WMSERIALNUMBER.R_Item).
 */
const CustomerSerialNumbers = (props) => {
    const { itemRowId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const allowedPageSizes = [10, 20, 100, 500, "all"];
    const stateKey = gridKey;
    const loadState = useCallback(() => GridLayout.sendStorageRequest(stateKey, "GET", accessToken, null, currentLayout), [stateKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(stateKey, "POST", accessToken, state, currentLayout), [stateKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!itemRowId) {
            return [];
        }
        const store = createStore({
            key: 'Rowid',
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.SerialNumberCodes.GetSerialNumbers}`,
            loadParams: { itemRowId: itemRowId },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        return new DataSource({ store });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemRowId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            dataSource={dataSource}
            keyExpr="Rowid"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={stateKey} customSave={saveState} />

            {/* Default-visible columns (mirrors the AI8 serial-number layout) */}
            <Column dataField="SerialNumber" caption="Serial Number" />
            <Column dataField="lotCode" caption="Lot Code" />
            <Column dataField="GrossWeight" caption="Gross Weight" dataType="number" format={numberFormat} />
            <Column dataField="NetWeight" caption="Net Weight" dataType="number" format={numberFormat} />
            <Column dataField="Size1" caption="Size 1" dataType="number" format={numberFormat} />
            <Column dataField="Size2" caption="Size 2" dataType="number" format={numberFormat} />
            <Column dataField="UserDefinedCode1" caption="Code 1" />
            <Column dataField="UserDefinedReference1" caption="Reference 1" />
            <Column dataField="DamageDescription" caption="Damage Description" />

            {/* Column-chooser (hidden by default) */}
            <Column dataField="itemCode" caption="Item Code" visible={false} />
            <Column dataField="RecordNumber" caption="Record #" visible={false} />
            <Column dataField="KeySequence" caption="Sequence" dataType="number" visible={false} />
            <Column dataField="LastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="LastUpdateDateTime" caption="Last Updated" dataType="date" visible={false} />
            <Column dataField="CreationUserId" caption="Created By" visible={false} />
            <Column dataField="CreationDateTime" caption="Creation Date" dataType="date" visible={false} />
            <Toolbar>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerSerialNumbers);
